import React, { useState, useEffect, useCallback } from 'react';
import { reportsApi, teamsApi, businessLinesApi, businessUnitsApi, hourMovementsApi } from '../services/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ChartBar, DownloadSimple, Eye, House, Briefcase, Phone, CalendarX, Clock } from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MONTHS = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];
const MOVEMENT_LABELS = { leave: 'Ferie', rol: 'ROL', unpaid: 'Non Retribuito' };

const ReportsPage = () => {
  const [teams, setTeams] = useState([]);
  const [businessLines, setBusinessLines] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [selectedBL, setSelectedBL] = useState('all');
  const [selectedBU, setSelectedBU] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [approvalStats, setApprovalStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  // Movement dialog
  const [movementsOpen, setMovementsOpen] = useState(false);
  const [movements, setMovements] = useState([]);
  const [movementUser, setMovementUser] = useState(null);

  const fetchFilters = useCallback(async () => {
    try {
      const [t, bl, bu] = await Promise.all([teamsApi.getAll(), businessLinesApi.getAll(), businessUnitsApi.getAll()]);
      setTeams(t.data); setBusinessLines(bl.data); setBusinessUnits(bu.data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = { month: selectedMonth, year: selectedYear };
      if (selectedTeam !== 'all') params.team_id = selectedTeam;
      else if (selectedBL !== 'all') params.business_line_id = selectedBL;
      else if (selectedBU !== 'all') params.business_unit_id = selectedBU;
      
      const [monthlyRes, approvalRes] = await Promise.all([
        reportsApi.monthly(params), reportsApi.approvalStats({})
      ]);
      setMonthlyReport(monthlyRes.data);
      setApprovalStats(approvalRes.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [selectedMonth, selectedYear, selectedTeam, selectedBL, selectedBU]);

  useEffect(() => { fetchFilters(); }, [fetchFilters]);
  useEffect(() => { fetchReports(); }, [fetchReports]);

  const exportExcel = () => {
    if (!monthlyReport?.employees?.length) { toast.info('Nessun dato da esportare'); return; }
    setExporting(true);
    try {
      const data = monthlyReport.employees.map(e => ({
        'Dipendente': e.name,
        'Email': e.email,
        'Ruolo': e.role,
        'Giorni Ufficio': e.office_days,
        'Giorni Smart Working': e.smart_working_days,
        'Giorni Ferie': e.leave_days,
        'Ore Permesso': e.permission_hours,
        'Giorni Reperibilità': e.on_call_days,
        'Straord. Ordinario (h)': e.overtime_ordinary,
        'Straord. Notturno (h)': e.overtime_night,
        'Straord. Festivo (h)': e.overtime_holiday,
        'Straord. Fest.+Nott. (h)': e.overtime_holiday_night,
        'Tot. Straordinario (h)': e.overtime_total,
        'Ore Lavorate': e.worked_hours,
        'Saldo Ferie (h)': e.leave_balance,
        'Saldo ROL (h)': e.rol_balance,
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, `${MONTHS[selectedMonth - 1]} ${selectedYear}`);
      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(new Blob([buf], { type: 'application/octet-stream' }), `report_${MONTHS[selectedMonth - 1]}_${selectedYear}.xlsx`);
      toast.success('Excel esportato');
    } catch (e) { toast.error('Errore export'); console.error(e); }
    finally { setExporting(false); }
  };

  const openMovements = async (emp) => {
    setMovementUser(emp);
    setMovementsOpen(true);
    try {
      const { data } = await hourMovementsApi.getByUser(emp.user_id);
      setMovements(data);
    } catch (e) { setMovements([]); }
  };

  const employees = monthlyReport?.employees || [];

  return (
    <div data-testid="reports-page">
      <Toaster position="top-right" richColors />
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 md:mb-6 gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[#0A0A0A]">Report</h1>
          <p className="text-[#52525B] mt-1">Report mensile per dipendente con export Excel</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={String(selectedMonth)} onValueChange={v => setSelectedMonth(Number(v))}>
            <SelectTrigger className="w-28 md:w-36 rounded-none border-[#E4E4E7] text-sm"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              {MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)} className="rounded-none">{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={String(selectedYear)} onValueChange={v => setSelectedYear(Number(v))}>
            <SelectTrigger className="w-24 rounded-none border-[#E4E4E7]"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              {[2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)} className="rounded-none">{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={exportExcel} disabled={exporting || !employees.length}
            className="bg-[#059669] text-white px-5 py-2.5 font-medium hover:bg-[#047857] rounded-none" data-testid="export-excel-btn">
            <DownloadSimple size={20} weight="bold" className="mr-2" />
            {exporting ? 'Esportazione...' : 'Esporta Excel'}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 md:gap-4 mb-4 md:mb-6 pb-4 border-b border-[#E4E4E7]">
        <Select value={selectedBU} onValueChange={v => { setSelectedBU(v); setSelectedBL('all'); setSelectedTeam('all'); }}>
          <SelectTrigger className="w-48 rounded-none border-[#E4E4E7]"><SelectValue placeholder="Business Unit" /></SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="all" className="rounded-none">Tutte le BU</SelectItem>
            {businessUnits.map(bu => <SelectItem key={bu.id} value={bu.id} className="rounded-none">{bu.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={selectedBL} onValueChange={v => { setSelectedBL(v); setSelectedTeam('all'); }}>
          <SelectTrigger className="w-48 rounded-none border-[#E4E4E7]"><SelectValue placeholder="Linea di Business" /></SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="all" className="rounded-none">Tutte le BL</SelectItem>
            {businessLines.map(bl => <SelectItem key={bl.id} value={bl.id} className="rounded-none">{bl.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={selectedTeam} onValueChange={setSelectedTeam}>
          <SelectTrigger className="w-48 rounded-none border-[#E4E4E7]"><SelectValue placeholder="Team" /></SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="all" className="rounded-none">Tutti i Team</SelectItem>
            {teams.map(t => <SelectItem key={t.id} value={t.id} className="rounded-none">{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#0A0A0A] border-t-transparent rounded-full animate-spin"></div></div>
      ) : employees.length === 0 ? (
        <div className="border border-[#E4E4E7] p-12 text-center">
          <ChartBar size={48} className="mx-auto text-[#E4E4E7] mb-4" />
          <h3 className="font-heading font-bold text-lg mb-2">Nessun dato</h3>
          <p className="text-[#52525B]">Nessun dipendente trovato per i filtri selezionati.</p>
        </div>
      ) : (
        <div className="border border-[#E4E4E7] overflow-x-auto">
          <table className="w-full data-table min-w-[1200px]">
            <thead>
              <tr className="border-b-2 border-[#0A0A0A] bg-white">
                <th className="py-3 px-3 text-left text-[10px] sticky left-0 bg-white z-10">Dipendente</th>
                <th className="py-3 px-3 text-center text-[10px]"><House size={14} className="mx-auto mb-0.5" />Ufficio</th>
                <th className="py-3 px-3 text-center text-[10px]"><Briefcase size={14} className="mx-auto mb-0.5 text-[#002FA7]" />Smart W.</th>
                <th className="py-3 px-3 text-center text-[10px]"><CalendarX size={14} className="mx-auto mb-0.5 text-[#F59E0B]" />Ferie (gg)</th>
                <th className="py-3 px-3 text-center text-[10px]"><Clock size={14} className="mx-auto mb-0.5 text-[#8B5CF6]" />Permessi (h)</th>
                <th className="py-3 px-3 text-center text-[10px]"><Phone size={14} className="mx-auto mb-0.5 text-[#FF2A00]" />Reperibilità</th>
                <th className="py-3 px-3 text-center text-[10px] bg-[#059669]/5">Str. Ord.</th>
                <th className="py-3 px-3 text-center text-[10px] bg-[#059669]/5">Str. Nott.</th>
                <th className="py-3 px-3 text-center text-[10px] bg-[#059669]/5">Str. Fest.</th>
                <th className="py-3 px-3 text-center text-[10px] bg-[#059669]/5">Tot. Str.</th>
                <th className="py-3 px-3 text-center text-[10px] font-black">Ore Lav.</th>
                <th className="py-3 px-3 text-center text-[10px]">Ferie (h)</th>
                <th className="py-3 px-3 text-center text-[10px]">ROL (h)</th>
                <th className="py-3 px-3 text-center text-[10px]">Storico</th>
              </tr>
            </thead>
            <tbody>
              {employees.map(emp => (
                <tr key={emp.user_id} className="border-b border-[#E4E4E7] hover:bg-[#F4F4F5]" data-testid={`report-row-${emp.user_id}`}>
                  <td className="py-2 px-3 font-medium text-sm sticky left-0 bg-white">{emp.name}</td>
                  <td className="py-2 px-3 text-center font-mono text-sm">{emp.office_days}</td>
                  <td className="py-2 px-3 text-center font-mono text-sm text-[#002FA7]">{emp.smart_working_days}</td>
                  <td className="py-2 px-3 text-center font-mono text-sm text-[#F59E0B]">{emp.leave_days}</td>
                  <td className="py-2 px-3 text-center font-mono text-sm text-[#8B5CF6]">{emp.permission_hours}</td>
                  <td className="py-2 px-3 text-center font-mono text-sm text-[#FF2A00]">{emp.on_call_days}</td>
                  <td className="py-2 px-3 text-center font-mono text-sm bg-[#059669]/5">{emp.overtime_ordinary}</td>
                  <td className="py-2 px-3 text-center font-mono text-sm bg-[#059669]/5">{emp.overtime_night}</td>
                  <td className="py-2 px-3 text-center font-mono text-sm bg-[#059669]/5">{emp.overtime_holiday}</td>
                  <td className="py-2 px-3 text-center font-mono text-sm font-bold bg-[#059669]/5">{emp.overtime_total}</td>
                  <td className="py-2 px-3 text-center font-mono text-sm font-black">{emp.worked_hours}</td>
                  <td className="py-2 px-3 text-center font-mono text-sm">{emp.leave_balance}</td>
                  <td className="py-2 px-3 text-center font-mono text-sm">{emp.rol_balance}</td>
                  <td className="py-2 px-3 text-center">
                    <button onClick={() => openMovements(emp)} className="w-7 h-7 flex items-center justify-center border border-[#E4E4E7] hover:border-[#0A0A0A] transition-colors mx-auto" title="Storico Movimentazioni">
                      <Eye size={14} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Approval Stats Summary */}
      {approvalStats && approvalStats.total > 0 && (
        <div className="mt-8 grid grid-cols-3 gap-4">
          <div className="border border-[#E4E4E7] p-4 text-center">
            <p className="text-2xl font-black text-[#F59E0B]">{approvalStats.by_status.pending || 0}</p>
            <p className="text-xs uppercase tracking-wider text-[#52525B] font-bold">In Attesa</p>
          </div>
          <div className="border border-[#E4E4E7] p-4 text-center">
            <p className="text-2xl font-black text-[#22C55E]">{approvalStats.by_status.approved || 0}</p>
            <p className="text-xs uppercase tracking-wider text-[#52525B] font-bold">Approvate</p>
          </div>
          <div className="border border-[#E4E4E7] p-4 text-center">
            <p className="text-2xl font-black text-[#FF2A00]">{approvalStats.by_status.rejected || 0}</p>
            <p className="text-xs uppercase tracking-wider text-[#52525B] font-bold">Rifiutate</p>
          </div>
        </div>
      )}

      {/* Hour Movements Dialog */}
      <Dialog open={movementsOpen} onOpenChange={setMovementsOpen}>
        <DialogContent className="sm:max-w-2xl rounded-none border border-[#0A0A0A] max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-xl font-bold">Storico Movimentazioni - {movementUser?.name}</DialogTitle></DialogHeader>
          {movements.length === 0 ? (
            <p className="text-center text-[#52525B] py-8">Nessuna movimentazione registrata</p>
          ) : (
            <div className="border border-[#E4E4E7] mt-4">
              <table className="w-full data-table">
                <thead><tr className="border-b-2 border-[#0A0A0A]">
                  <th className="py-3 px-3 text-left">Data</th>
                  <th className="py-3 px-3 text-left">Tipo</th>
                  <th className="py-3 px-3 text-center">Ore</th>
                  <th className="py-3 px-3 text-left">Motivo</th>
                  <th className="py-3 px-3 text-left">Origine</th>
                </tr></thead>
                <tbody>
                  {movements.map(m => (
                    <tr key={m.id} className="border-b border-[#E4E4E7]">
                      <td className="py-2 px-3 font-mono text-xs">{new Date(m.created_at).toLocaleDateString('it-IT')}</td>
                      <td className="py-2 px-3">{MOVEMENT_LABELS[m.type] || m.type}</td>
                      <td className={`py-2 px-3 text-center font-mono font-bold ${m.hours > 0 ? 'text-[#22C55E]' : 'text-[#FF2A00]'}`}>
                        {m.hours > 0 ? '+' : ''}{m.hours}h
                      </td>
                      <td className="py-2 px-3 text-sm text-[#52525B]">{m.reason}</td>
                      <td className="py-2 px-3 text-xs text-[#52525B]">{m.source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReportsPage;
